/* eslint-disable no-case-declarations */
import { Col, Row } from 'antd';
import Avatar from 'antd/lib/avatar/avatar';
import { ButtonWithSpinnerType } from 'components/common/ButtonWithSpinner/types';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import moment from 'moment';
import React, { useMemo } from 'react';
import { TextOnlyButtonStyled } from 'routes/Transaction/components/styled';
import { AssignmentStatus } from 'types/appointmentAssignments';
import { AppointmentStatus, DocumentStatus } from 'types/appointments';
import { RoleEnum } from 'types/user';
import { getDefaultAvatarUrl, getStatusColor } from 'utils';

import { NewCardActionButtonStyled } from '../../styled';
import { NotaryNameStyled } from './styled';
import { IAppointmentCardActionButtons } from './types';

const CardActionButtonContainer: React.FC<{ single?: boolean } & ButtonWithSpinnerType> = ({
  children,
  single,
  ...props
}) => (
  <div className={`${single ? 'w-full' : ''} md:w-full flex items-center`}>
    <NewCardActionButtonStyled width='auto' height='auto' buttonType='text' color='#1E73BE' {...props}>
      <span className='md:text-left md:py-0 py-2'>{children}</span>
    </NewCardActionButtonStyled>
  </div>
);

const CardActionButton: React.FC<IAppointmentCardActionButtons> = ({
  appointment,
  assignNotaryClick,
  acceptAppointmentClick,
  rejectAppointmentClick,
  completeAppointmentClick,
  cancelAppointmentClick,
  markAppointmentMailedClick,
  markAppointmentUploadedClick,
  uploadDocumentClick,
}) => {
  const { role, id } = useCurrentLoggedInUser(); // test nha
  const acceptedNotary = appointment.notary;
  const requireScanDoc = appointment.transaction?.requiredScanDoc;
  const { documentStatus, status } = appointment;
  const appointmentEndTime = appointment.date;
  const isAppointmentPast = moment(appointmentEndTime).isBefore(Date.now());
  const appointmentAssignments = appointment.assignments;
  const isNotaryRequested = appointmentAssignments && appointmentAssignments.length > 0;

  const ActionButton = useMemo(() => {
    if (isAppointmentPast && status === AppointmentStatus.COMPLETED && documentStatus === DocumentStatus.MAILED) {
      return (
        <CardActionButtonContainer single color='#fa8c15'>
          Documents Mailed
        </CardActionButtonContainer>
      );
    }
    if (appointment && status === AppointmentStatus.CANCELLED) {
      return (
        <CardActionButtonContainer single color={getStatusColor(appointment.status)}>
          Cancelled
        </CardActionButtonContainer>
      );
    }

    switch (role) {
      case RoleEnum.ADMIN:
        return (
          <div className='flex items-center'>
            {!!acceptedNotary && (
              <>
                <Row align='middle' justify='end' className='gx-m-0 gx-flex-nowrap'>
                  <Col style={{ width: 32 }}>
                    <Avatar
                      src={
                        acceptedNotary.avatar?.thumbnailPipeFrom ||
                        getDefaultAvatarUrl(`${acceptedNotary.firstName} ${acceptedNotary.lastName}`)
                      }
                    />
                  </Col>
                  <Col>
                    <NotaryNameStyled $color='black' className='gx-m-0 gx-pl-1 gx-font-weight-medium '>
                      {acceptedNotary.firstName} {acceptedNotary.lastName}
                    </NotaryNameStyled>
                  </Col>
                </Row>
                {!isAppointmentPast && (
                  <div className='gx-pl-2'>
                    <TextOnlyButtonStyled
                      onClick={(e) => {
                        e.stopPropagation();
                        assignNotaryClick?.(appointment);
                      }}>
                      Change
                    </TextOnlyButtonStyled>
                  </div>
                )}
              </>
            )}
            {isNotaryRequested && !acceptedNotary && (
              <div className='flex'>
                <CardActionButtonContainer color='#fa8c15'>Requested</CardActionButtonContainer>
                <CardActionButtonContainer
                  className='gx-p-0 gx-pl-2'
                  onClick={(e) => {
                    e.stopPropagation();
                    assignNotaryClick?.(appointment);
                  }}
                  color='#038fde'>
                  Detail
                </CardActionButtonContainer>
              </div>
            )}
            {!isNotaryRequested && !acceptedNotary && (
              <CardActionButtonContainer
                single
                color='#038fde'
                onClick={(e) => {
                  e.stopPropagation();
                  assignNotaryClick?.(appointment);
                }}>
                Request Notary
              </CardActionButtonContainer>
            )}
          </div>
        );

      case RoleEnum.NOTARY:
        // eslint-disable-next-line no-case-declarations
        const ownedAssignment = appointmentAssignments?.find((assignment) => assignment.notary.id === id);
        const assignmentStatus = ownedAssignment?.status;
        return (
          <>
            {assignmentStatus === AssignmentStatus.PENDING &&
              (!isAppointmentPast ? (
                <div className='flex'>
                  <CardActionButtonContainer
                    onClick={(e) => {
                      e.stopPropagation();
                      acceptAppointmentClick?.(ownedAssignment);
                    }}
                    color='#038fde'>
                    Accept
                  </CardActionButtonContainer>
                  <CardActionButtonContainer
                    onClick={(e) => {
                      e.stopPropagation();
                      rejectAppointmentClick?.(ownedAssignment);
                    }}
                    color='#FF0000'>
                    Reject
                  </CardActionButtonContainer>
                </div>
              ) : (
                <CardActionButtonContainer single color='FF0000'>
                  Overdue
                </CardActionButtonContainer>
              ))}
            {assignmentStatus === AssignmentStatus.REJECTED && (
              <CardActionButtonContainer single color='FF0000'>
                Rejected
              </CardActionButtonContainer>
            )}
            {assignmentStatus === AssignmentStatus.APPROVED &&
              status !== AppointmentStatus.COMPLETED &&
              (isAppointmentPast ? (
                <CardActionButtonContainer
                  single
                  onClick={(e) => {
                    e.stopPropagation();
                    completeAppointmentClick?.();
                  }}
                  color='#1E73BE'>
                  Mark as Completed
                </CardActionButtonContainer>
              ) : (
                !!cancelAppointmentClick && (
                  <CardActionButtonContainer
                    single
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelAppointmentClick(ownedAssignment);
                    }}
                    color='#FF0000'>
                    Cancel Appointment
                  </CardActionButtonContainer>
                )
              ))}
            {status === AppointmentStatus.COMPLETED && (
              <>
                {documentStatus === DocumentStatus.NONE && requireScanDoc && (
                  <div className='flex md:flex-col w-full'>
                    <CardActionButtonContainer
                      onClick={(e) => {
                        e.stopPropagation();
                        uploadDocumentClick?.();
                      }}>
                      Upload Documents
                    </CardActionButtonContainer>
                    <CardActionButtonContainer
                      className='md:mt-2'
                      onClick={(e) => {
                        e.stopPropagation();
                        markAppointmentUploadedClick?.();
                      }}>
                      Mark As Documents Scanned
                    </CardActionButtonContainer>
                  </div>
                )}
                {(documentStatus === DocumentStatus.SCANNED || !requireScanDoc) && (
                  <div className='flex md:flex-col w-full'>
                    <CardActionButtonContainer
                      className='md:mt-2'
                      onClick={(e) => {
                        e.stopPropagation();
                        markAppointmentMailedClick?.();
                      }}>
                      Mark As Documents Mailed
                    </CardActionButtonContainer>
                  </div>
                )}
              </>
            )}
          </>
        );

      case RoleEnum.CLIENT:
      default:
        return (
          <>
            {!!acceptedNotary && (
              <Col className='gx-pl-0'>
                <Row align='middle' justify='end' className='gx-m-0'>
                  <Col style={{ width: 32 }}>
                    <Avatar
                      src={
                        acceptedNotary.avatar?.thumbnailPipeFrom ||
                        getDefaultAvatarUrl(`${acceptedNotary.firstName} ${acceptedNotary.lastName}`)
                      }
                    />
                  </Col>
                  <Col>
                    <NotaryNameStyled $color='black' className='gx-m-0 gx-pl-1 gx-font-weight-medium'>
                      {acceptedNotary.firstName} {acceptedNotary.lastName}
                    </NotaryNameStyled>
                  </Col>
                </Row>
              </Col>
            )}
            {!acceptedNotary &&
              (isNotaryRequested ? (
                <CardActionButtonContainer single>Waiting for Notary Approval</CardActionButtonContainer>
              ) : (
                <CardActionButtonContainer single color='#fa8c15'>
                  Awaiting Notary Assignment
                </CardActionButtonContainer>
              ))}
          </>
        );
    }
  }, [appointment]);

  return <div className='flex items-center md:items-start'>{ActionButton}</div>;
};

export default React.memo(CardActionButton);
